import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, vShow as _vShow, withDirectives as _withDirectives, normalizeStyle as _normalizeStyle, resolveComponent as _resolveComponent, renderList as _renderList } from "vue"

const _hoisted_1 = ["draggable", "id"]
const _hoisted_2 = { class: "folder__icon-slot" }
const _hoisted_3 = { class: "folder__input" }
const _hoisted_4 = { class: "folder__name" }
const _hoisted_5 = ["innerHTML"]

import {
  ref,
  computed,
  inject,
  onMounted,
  watch,
  Ref,
  nextTick,
  onUnmounted,
} from "vue";
import FileInput from "./FileInput.vue";
import {
  DragEntity,
  DragGeneralState,
  DropValidationResult,
  FileOut,
  FolderOut,
} from "@/models/interfaces/file/file-model";
import AppIcon from "../ui-kit/icon/AppIcon.vue";
import AppHashtag from "../ui-kit/hashtag/AppHashtag.vue";
import AppTooltip from "../ui-kit/tooltip/AppTooltip.vue";
import FileString from "@/components/file/FileString.vue";
import fileMenu from "./FileMenu.vue";
import AppCheckbox from "../ui-kit/checkbox/AppCheckbox.vue";
import FileDropdown from "./FileDropdown.vue";
import { ProjectContainer } from "@/models/interfaces/project/project-models";
import { FileApi } from "@/ api/FileApi";
import { TagBaseType, TagEntityType } from "@/models/etc/types";
import { TagWithFrequency } from "@/models/interfaces/tags/tag-models";
import { escapeHtml, highlightFileSearchText } from "@/utils/textHelpers";
import { i18n } from "@/i18n";

interface Props {
  folderType?:
    | "projectFolder"
    | "systemFolder"
    | "systemProjectList"
    | "default";
  newDir?: boolean;
  folder: FolderOut;
  rootFolder?: boolean;
  parentNode?: FolderOut;
  searchMode?: boolean;
  ruleAccess: number;
  level?: number;
  siblingFolderNames?: string[];
}

type FolderTag = FolderOut["Tags"][number];
interface ChildEventData {
  type: string;
  data: any;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FileFolder',
  props: {
    folderType: { default: "default" },
    newDir: { type: Boolean },
    folder: { default: undefined },
    rootFolder: { type: Boolean, default: false },
    parentNode: {},
    searchMode: { type: Boolean, default: false },
    ruleAccess: {},
    level: { default: 0 },
    siblingFolderNames: { default: () => [] }
  },
  emits: ["open", "create"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const siblingFolderNames = computed(() => {
  if (props.newDir) return props.siblingFolderNames;

  return (props.parentNode?.Folders ?? [])
    .filter((item) => item.Id !== props.folder.Id)
    .map((item) => item.Name);
});

const emit = __emit;

const pendingFolderNames = ref<string[]>([]);

const isSystemFolderName = (name: string, key: string): boolean => {
  const currentName = String(i18n.global.t(key));
  const russianMessages = i18n.global.getLocaleMessage("ru") as Record<string, unknown>;
  return name === currentName || name === String(russianMessages[key] ?? "");
};

const generateUniqueFolderName = (): string => {
  const baseName = String(i18n.global.t("file_new_folder"));

  const existingNames = [
    ...(props.folder.Folders ?? []).map((folder) => folder.Name),
    ...pendingFolderNames.value,
  ].map((name) => name.trim().toLowerCase());

  if (!existingNames.includes(baseName.toLowerCase())) {
    return baseName;
  }

  let counter = 1;
  let newName = `${baseName} (${counter})`;

  while (existingNames.includes(newName.toLowerCase())) {
    counter++;
    newName = `${baseName} (${counter})`;
  }

  return newName;
};

let isOpen = ref(false);
let isEditable = ref(false);
let isNameEditable = ref(false);
let isCreateDir = ref(false);
let isDragging = ref(false);
let actionDone = ref(false);
let newFolder = ref<FolderOut>({
  Id: 0,
  Name: String(i18n.global.t("file_new_folder")),
  Editable: true,
  Files: [],
  Folders: [],
  ProjectId: props.folder.ProjectId,
  Tags: [],
  isSelected: false,
});

let isDragStart = ref(false);
let isDragValid = ref(true);
let isDragSource = ref(false);

let levelStyle = computed(() => {
  return {
    paddingLeft: `${props.level == 0 ? 2 : props.level * 16}px`,
  };
});
const inputRef = ref<InstanceType<typeof FileInput> | null>(null);
const fileInputRef = ref<HTMLInputElement>();
const folderRef = ref<HTMLElement | null>(null);
const isMultiline = ref(false);
let folderResizeObserver: ResizeObserver | null = null;

const hasInaccessibleDescendants = computed(() => {
  const hasInaccessibleElements = (folder: FolderOut): boolean =>
    (folder.Files ?? []).some((file) => file.Editable === false) ||
    (folder.Folders ?? []).some(
      (child) =>
        child.Editable === false || hasInaccessibleElements(child),
    );

  return hasInaccessibleElements(props.folder);
});

const advancedMode = inject("mode");
const searchMode = inject<Ref<boolean>>("searchMode");
const searchText = inject<Ref<string>>("searchText");
const containerProject = inject<Ref<ProjectContainer>>("containerProject");
const isAdmin = inject("isAdmin");
const newDirFocus = inject<Ref<boolean>>("newDirFocus", ref(false));
const dirFocusId = inject<Ref<string>>("dirFocusId");
const disableNewDirFocus = inject<Function>("disableNewDirFocus");
const changeElFocusId = inject<Function>("changeElFocusId");
const renameFolderFocus = inject<Ref<boolean>>("renameFolderFocus", ref(false));
const disableRenameFolderFocus = inject<Function>("disableRenameFolderFocus");
const dropdownMenu = ref<InstanceType<typeof FileDropdown> | null>(null);
const searchOpenedFolderIds = inject<Ref<number[]>>("searchOpenedFolderIds");
const openAfterMoveFolderId = inject<Ref<number | null>>(
  "openAfterMoveFolderId",
  ref(null),
);

const changeModalPos = inject<Function>("changeModalPos");

const menuAnchorRef = ref<HTMLElement | null>(null);

const isFileUploading = ref(false);
let newFile = ref<FileOut>({
  Id: 0,
  Name: String(i18n.global.t("file_new_file")),
  Tags: [],
  JobBindings: [],
  isSelected: false,
  Visible: false,
  GroupsWithAccess: [],
  Signed: false,
  Editable: false,
});

const setModalPositionNearDropdown = () => {
  const el = menuAnchorRef.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();

  changeModalPos?.({
    x: rect.right + window.scrollX + 8,
    y: rect.top + window.scrollY,
  });
};

const dragGeneralState = inject<DragGeneralState>("dragGeneralState", {
  name: "",
  type: "",
  count: 0,
  active: false,
  isValid: true,
  x: 0,
  y: 0,
});

const setDragState = inject<Function>("setDragState");

const selectedTags = ref<TagWithFrequency[]>([]);
const isTagsUpdating = ref(false);

const mapFolderTagToSelectorTag = (tag: FolderTag): TagWithFrequency => ({
  ...tag,
  Frequency: 0,
  FullName: tag.Name,
  Type: TagBaseType.Common,
});

const mapSelectorTagToFolderTag = (tag: TagWithFrequency): FolderTag => ({
  Signed: false,
  SignedBy: "",
  SignedAt: null as unknown as Date,
  Id: tag.Id,
  Name: tag.Name,
});
const isSameTag = (
  a: { Id: any; Name: string },
  b: { Id: any; Name: string },
): boolean => {
  const aIdExists = a.Id !== null && a.Id !== undefined;
  const bIdExists = b.Id !== null && b.Id !== undefined;

  if (aIdExists && bIdExists) {
    return String(a.Id) === String(b.Id);
  }

  return a.Name.trim().toLowerCase() === b.Name.trim().toLowerCase();
};

watch(
  () => props.folder.Tags,
  (tags) => {
    selectedTags.value = (tags ?? []).map(mapFolderTagToSelectorTag);
  },
  { immediate: true, deep: true },
);

const changeTags = async (newSelectedTags: TagWithFrequency[]) => {
  if (isTagsUpdating.value) return;

  const currentFolderTags: FolderTag[] = props.folder.Tags ?? [];
  const nextSelectedTags: TagWithFrequency[] = newSelectedTags ?? [];

  const addedTags = nextSelectedTags.filter(
    (selectedTag) =>
      !currentFolderTags.some((folderTag) => isSameTag(folderTag, selectedTag)),
  );

  const removedTags = currentFolderTags.filter(
    (folderTag) =>
      !nextSelectedTags.some((selectedTag) =>
        isSameTag(folderTag, selectedTag),
      ),
  );

  if (addedTags.length === 0 && removedTags.length === 0) {
    return;
  }

  isTagsUpdating.value = true;

  try {
    const success = await emitToParent?.({
      type: "changeFolderTags",
      data: {
        dir: props.folder,
        tags: nextSelectedTags.map(mapSelectorTagToFolderTag),
        addedTags,
        removedTags,
      },
    });

    if (!success) {
      selectedTags.value = currentFolderTags.map(mapFolderTagToSelectorTag);
    }
  } catch (error) {
    selectedTags.value = currentFolderTags.map(mapFolderTagToSelectorTag);
  } finally {
    isTagsUpdating.value = false;
  }
};

const onFolderTagCreated = async (tags: FolderTag[]) => {
  selectedTags.value = (tags ?? []).map(mapFolderTagToSelectorTag);

  await emitToParent?.({
    type: "setFolderTags",
    data: {
      dir: props.folder,
      tags,
    },
  });
};

const removeTag = async (item: { Id: any; Name: string }) => {
  if (isTagsUpdating.value) return;

  const nextSelectedTags = (selectedTags.value ?? []).filter(
    (tag) => !isSameTag(tag, item),
  );

  await changeTags(nextSelectedTags);
};

const isSearched = computed(() => {
  if (!searchMode?.value || !searchText?.value) return false;

  const folderName = props.folder?.Name?.toLowerCase() ?? "";
  return searchText.value
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .some((word) => folderName.includes(word));
});

const highlightedName = computed(() => {
  const escapedName = escapeHtml(props.folder.Name);
  if (!searchMode?.value || !searchText?.value) return escapedName;

  return highlightFileSearchText(searchText.value, escapedName);
});

const emitToParent =
  inject<(eventData: ChildEventData) => boolean | Promise<boolean>>(
    "childEventEmitter",
  );

const menuElementsParent = computed(() => {
  if ((props.ruleAccess == 2 && props.folder.Editable) || isAdmin) {
    return [
      { name: String(i18n.global.t("file_upload")), nameIcon: "clip", event: "upload" },
      {
        name: String(i18n.global.t("file_create_nested_folder")),
        nameIcon: "folder-closed-add",
        event: "create",
      },
      { name: String(i18n.global.t("file_download_folder")), nameIcon: "download", event: "download" },
      {
        name: String(i18n.global.t("file_view_information")),
        nameIcon: "info-circle-outline",
        event: "infoParent",
      },
    ];
  } else if (props.ruleAccess !== 2 && props.folder.Editable) {
    return [
      { name: String(i18n.global.t("file_download_folder")), nameIcon: "download", event: "download" },
      {
        name: String(i18n.global.t("file_view_information")),
        nameIcon: "info-circle-outline",
        event: "infoParent",
      },
    ];
  }

  return [
    {
      name: String(i18n.global.t("file_view_information")),
      nameIcon: "info-circle-outline",
      event: "infoParent",
    },
  ];
});

const menuElementsInserted = computed(() => {
  if ((props.ruleAccess == 2 && props.folder.Editable) || isAdmin) {
    return [
      { name: String(i18n.global.t("file_upload")), nameIcon: "clip", event: "upload" },
        {
          name: String(i18n.global.t("file_create_nested_folder")),
          nameIcon: "folder-closed-add",
          event: "create",
        },
        {
          name: String(i18n.global.t("file_rename_folder")),
          nameIcon: "edit-pencil",
          event: "rename",
        },
        { name: String(i18n.global.t("file_download_folder")), nameIcon: "download", event: "download" },
        {
          name: String(i18n.global.t("file_view_information")),
          nameIcon: "info-circle-outline",
          event: "info",
        },
        { name: String(i18n.global.t("remove")), nameIcon: "delete-outline", event: "remove" },
    ];
  } else if (props.ruleAccess !== 2 && props.folder.Editable) {
    return [
      { name: String(i18n.global.t("file_download_folder")), nameIcon: "download", event: "download" },
        {
          name: String(i18n.global.t("file_view_information")),
          nameIcon: "info-circle-outline",
          event: "info",
        },
    ];
  }

  return [
    {
      name: String(i18n.global.t("file_view_information")),
      nameIcon: "info-circle-outline",
      event: "infoParent",
    },
  ];
});

const selectFilesForUpload = () => {
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
};

const handleFileSelect = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  if (!input.files || input.files.length === 0) return;

  const files = Array.from(input.files);

  if (!isOpen.value) {
    isOpen.value = true;
  }

  for (const file of files) {
    const formData = new FormData();

    const maxSize = 100 * 1024 * 1024;
    if (file.size > maxSize) {
      await emitToParent?.({
        type: "error",
        data: {
          type: "error",
          errorType: "size",
        },
      });
      continue;
    }

    newFile.value = {
      ...newFile.value,
      Name: file.name,
    };

    isFileUploading.value = true;

    formData.append("File", file);
    formData.append("FolderId", props.folder.Id.toString());
    formData.append("FileName", file.name);

    try {
      await emitToParent?.({
        type: "upload",
        data: {
          formdata: formData,
        },
      });
    } finally {
      isFileUploading.value = false;
    }
  }

  input.value = "";
};

const blurInput = () => {
  isNameEditable.value = false;

  if (props.newDir && searchMode?.value) {
    isNameEditable.value = true;
  }
};

const handleOpenClick = () => {
  if ((containerProject?.value?.FileAccess == 0 && !isAdmin) || props.newDir) {
    return;
  }

  const newState = !isOpen.value;
  isOpen.value = newState;

  emitToParent?.({
    type: "toggleFolder",
    data: {
      folderId: props.folder.Id,
      isOpen: newState,
      folder: props.folder,
      mode: searchMode?.value ? "search" : "default",
    },
  });

  if (isOpen.value && props.folderType == "default") {
    emitToParent?.({ type: "open", data: { dir: props.folder } });
  } else if (
    isOpen.value &&
    props.folderType == "projectFolder"
  ) {
    emitToParent?.({ type: "openProject", data: { dir: props.folder } });
  }
};

const selectFolder = (value: boolean) => {
  if (emitToParent) {
    emitToParent({ type: "select", data: { dir: props.folder, value: value } });
  }
};

const selectFile = (file: FileOut, value: boolean) => {
  if (emitToParent) {
    emitToParent({
      type: "selectFile",
      data: { dir: props.folder, file: file, value: value },
    });
  }
};

const renameFile = (file: FileOut, value: boolean) => {
  if (emitToParent) {
    emitToParent({
      type: "renameFile",
      data: { dir: props.folder, file: file, value: value },
    });
  }
};

const replaceFile = (file: FileOut, value: string, form: FormData) => {
  form.append("FileId", file.Id);
  form.append("FolderId", props.folder.Id);
  form.append("Filename", value);
  form.append("KeepTags", "true");

  if (emitToParent) {
    emitToParent({
      type: "replace",
      data: { formdata: form },
    });
  }
};

const chooseAction = (option: string) => {
  if (option == "rename" && inputRef.value) {
    inputRef.value.focus();
    return;
  }

  if (option == "create") {
    createDir();
    return;
  }

  if (option == "upload") {
    selectFilesForUpload();
    return;
  }

  if (emitToParent) {
    emitToParent({
      type: option,
      data: { dir: props.folder },
    });
  }
};

const chooseFileAction = (file: FileOut, event: string) => {
  if (emitToParent) {
    emitToParent({
      type: event,
      data: { dir: props.folder, file: file },
    });
  }
};

const renameFolder = (name: string) => {
  if (!emitToParent) return;

  if (props.newDir === true) {
    emit("create", name);
    return;
  }

  changeElFocusId?.(props.folder.Id);

  emitToParent({
    type: "rename",
    data: { dir: props.folder, value: name },
  });
};

const handleMouseEnter = () => {
  isEditable.value = true;
};

const handleMouseLeave = () => {
  isEditable.value = false;
  dropdownMenu.value?.closeDropdown();
};

const createDir = async () => {
  if (containerProject?.value.FileAccess !== 2 && !isAdmin) return;

  if (isOpen.value == false) {
    if (
      props.folderType !== "systemFolder" &&
      props.folderType !== "systemProjectList"
    ) {
      if (emitToParent) {
        if (props.folderType == "projectFolder") {
          actionDone.value = await emitToParent({
            type: "openProject",
            data: { dir: props.folder },
          });
        } else {
          actionDone.value = await emitToParent({
            type: "open",
            data: { dir: props.folder },
          });
        }
      }
    }
  } else {
    isCreateDir.value = true;
    newFolder.value.Name = generateUniqueFolderName();
  }

  isOpen.value = true;
};

watch(
  () => actionDone?.value,
  async (val) => {
    if (!val) return;

    await nextTick();

    isCreateDir.value = true;
    newFolder.value.Name = generateUniqueFolderName();
  },
);

const createDirSuccess = async (name: string) => {
  isOpen.value = true;

  const normalizedName = name.trim().toLowerCase();
  const exists = (props.folder.Folders ?? []).some(
    (f) => f.Name.trim().toLowerCase() === normalizedName,
  );

  if (exists) {
    newFolder.value = {
      ...newFolder.value,
      Name: name,
    };

    isCreateDir.value = true;
    isNameEditable.value = false;

    if (emitToParent) {
      emitToParent({
        type: "error",
        data: {
          type: "error",
          errorType: "folderNameMatch",
          focusTarget: "new-folder",
        },
      });
    }

    return;
  }

  isCreateDir.value = false;
  newFolder.value = {
    Id: 0,
    Name: String(i18n.global.t("file_new_folder")),
    Editable: true,
    Files: [],
    Folders: [],
    ProjectId: props.folder.ProjectId,
    Tags: [],
    isSelected: false,
  };

  if (emitToParent) {
    pendingFolderNames.value.push(name);

    try {
      await emitToParent({
        type: "create",
        data: { dir: props.folder, value: name },
      });
    } finally {
      pendingFolderNames.value = pendingFolderNames.value.filter(
        (pendingName) => pendingName !== name,
      );
    }
  }
};

const openSearchFoldersIdList = inject<Ref<number[]>>(
  "openSearchFoldersIdList",
);
const handleClearSearch = inject<Ref<number[]>>("handleClearSearch");

watch(
  () => ({
    search: !!searchMode?.value,
    opened: !!searchOpenedFolderIds?.value.includes(props.folder.Id),
  }),
  ({ search, opened }) => {
    if (search && !props.newDir) {
      isOpen.value = opened;
    }
  },
  { immediate: true },
);

watch(
  () => newDirFocus.value,
  (newValue) => {
    if (newValue === true && props.newDir === true) {
      isNameEditable.value = true;

      nextTick(() => {
        inputRef.value?.focus();
      });

      disableNewDirFocus?.();
    }
  },
);

watch(
  () => renameFolderFocus.value,
  (newValue) => {
    if (newValue === true && dirFocusId?.value == props.folder.Id) {
      isNameEditable.value = true;

      nextTick(() => {
        inputRef.value?.focus();
      });

      disableRenameFolderFocus?.();
    }
  },
);

watch(
  () => openAfterMoveFolderId.value,
  (folderId) => {
    if (folderId === props.folder.Id) {
      isOpen.value = true;
    }
  },
);

onMounted(() => {
  folderResizeObserver = new ResizeObserver(([entry]) => {
    const content = entry.target.querySelector<HTMLElement>(".folder__input");
    isMultiline.value = (content?.getBoundingClientRect().height ?? 0) > 20;
  });

  if (folderRef.value) {
    folderResizeObserver.observe(folderRef.value);
  }

  if (props.newDir == true) {
    isNameEditable.value = true;

    nextTick(() => {
      inputRef.value?.focus();
    });
  }
  if (props.rootFolder == true) {
    isOpen.value = true;
  }
});

onUnmounted(() => {
  folderResizeObserver?.disconnect();
});

watch(
  () => props.folder.Id,
  () => {
    if (
      (props.folderType == "systemFolder" &&
        isSystemFolderName(props.folder.Name, "file_search_results")) ||
      props.rootFolder
    )
      return;

    isOpen.value = false;
  },
);

watch(
  () => handleClearSearch?.value,
  () => {
    if (props.rootFolder) return;

    if (props.folderType == "systemFolder") {
      isOpen.value = false;
    }
  },
);

watch(
  () => openSearchFoldersIdList?.value.includes(props.folder.Id),
  (shouldOpen) => {
    if (searchMode?.value) return;

    if (shouldOpen) {
      isOpen.value = true;
    }
  },
  { immediate: true },
);

const getSelectedDragEntity = inject<() => DragEntity>("getSelectedDragEntity");

const handleDragStart = (event: DragEvent) => {
  const sel = window.getSelection();
  if (sel && !sel.isCollapsed) {
    event.preventDefault();
    sel.removeAllRanges();
    return;
  }

  if (!advancedMode.value) {
    event.preventDefault();
    return;
  }

  if (!event.dataTransfer) return;

  const img = document.createElement("div");
  img.style.width = "0";
  img.style.height = "0";
  document.body.appendChild(img);
  event.dataTransfer.setDragImage(img, 0, 0);
  event.dataTransfer.effectAllowed = "move";

  dragGeneralState.active = true;
  dragGeneralState.x = event.clientX;
  dragGeneralState.y = event.clientY;

  let draggedEntity: DragEntity = {
    type: "folder",
    folder: props.folder,
  };

  const selection = getSelectedDragEntity?.();
  if (selection && selection.type === "selection") {
    const selectedFolderIds = selection.folders.map((f) => f.Id);
    const selectedFileIds = selection.files.map((f) => f.file.Id);

    const folderSelected = selectedFolderIds.includes(props.folder.Id);
    const hasAnySelection =
      selection.folders.length > 0 || selection.files.length > 0;

    if (folderSelected && hasAnySelection) {
      draggedEntity = selection;
    }
  }

  event.dataTransfer.setData(
    "application/json",
    JSON.stringify({
      type: draggedEntity.type,
    }),
  );

  setDragState?.({
    draggedEntity,
    draggedElement: event.target as HTMLElement,
    dropTarget: null,
    isValidDrop: false,
  });

  isDragSource.value = true;
};

function onDrag(e: DragEvent) {
  if (!e.clientX && !e.clientY) return;

  dragGeneralState.x = e.clientX;
  dragGeneralState.y = e.clientY;
}

const handleDragEnd = () => {
  setDragState?.({
    draggedEntity: null,
    draggedElement: null,
    dropTarget: null,
    isValidDrop: false,
  });

  dragGeneralState.active = false;
  isDragSource.value = false;
  isDragStart.value = false;
};

const dragState = inject<{
  draggedEntity: DragEntity | null;
  dropTarget: FolderOut | null;
  isValidDrop: boolean;
}>("dragState", {
  draggedEntity: null,
  dropTarget: null,
  isValidDrop: false,
});

const hoverValidationCache = new Map<number, FolderOut>();
const hoverValidationPending = new Map<number, Promise<FolderOut>>();
let dragOverValidationToken = 0;

const getTargetChildrenForValidation = async (
  targetId: number,
): Promise<FolderOut> => {
  const cached = hoverValidationCache.get(targetId);
  if (cached) {
    return cached;
  }

  const pending = hoverValidationPending.get(targetId);
  if (pending) {
    return pending;
  }

  const request = FileApi.getChildFolders(targetId)
    .then((result) => {
      hoverValidationCache.set(targetId, result);
      hoverValidationPending.delete(targetId);
      return result;
    })
    .catch((error) => {
      hoverValidationPending.delete(targetId);
      throw error;
    });

  hoverValidationPending.set(targetId, request);

  return request;
};

const cloneTargetWithFreshChildren = (
  target: FolderOut,
  freshTarget: FolderOut,
): FolderOut => {
  return {
    ...target,
    Folders: freshTarget.Folders ?? [],
    Files: freshTarget.Files ?? [],
  };
};

const shouldFetchTargetForValidation = (target: FolderOut): boolean => {
  if (target.Id === 0) return false;
  if (props.folderType === "projectFolder") return false;
  if (isOpen.value) return false;
  return true;
};

const validateDropAsync = async (
  draggedEntity: DragEntity,
  target: FolderOut,
): Promise<DropValidationResult> => {
  const localValidation = validateDrop(draggedEntity, target);
  if (!localValidation.isValid) {
    return localValidation;
  }

  if (!shouldFetchTargetForValidation(target)) {
    return localValidation;
  }

  const freshTarget = await getTargetChildrenForValidation(target.Id);
  const hydratedTarget = cloneTargetWithFreshChildren(target, freshTarget);

  return validateDrop(draggedEntity, hydratedTarget);
};

const handleDragOver = async (event: DragEvent) => {
  const folderElement = (event.currentTarget as HTMLElement).closest(".folder");
  if (!folderElement) return;

  const hasExternalFiles = Array.from(event.dataTransfer?.types ?? []).includes(
    "Files",
  );

  if (hasExternalFiles) {
    const isValidExternalTarget =
      props.folder.Id !== 0 &&
      !props.newDir &&
      props.folderType !== "systemProjectList" &&
      props.folder.Editable &&
      (containerProject?.value?.FileAccess === 2 || isAdmin);

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer!.dropEffect = isValidExternalTarget ? "copy" : "none";
    isDragStart.value = true;
    isDragValid.value = isValidExternalTarget;
    return;
  }

  if((props.ruleAccess !== 2 || !props.folder.Editable))
  {
    isDragValid.value = false;
    return;
  }

  event.preventDefault();
  event.stopPropagation();

  if (!dragState.draggedEntity) return;

  isDragging.value = true;

  const currentToken = ++dragOverValidationToken;

  const fastValidation = validateDrop(dragState.draggedEntity, props.folder);
  event.dataTransfer!.dropEffect = fastValidation.isValid ? "move" : "none";
  isDragValid.value = fastValidation.isValid;

  setDragState?.({
    dropTarget: props.folder,
    isValidDrop: fastValidation.isValid,
  });

  if (!fastValidation.isValid) {
    return;
  }

  const asyncValidation = await validateDropAsync(
    dragState.draggedEntity,
    props.folder,
  );

  if (currentToken !== dragOverValidationToken) {
    return;
  }

  event.dataTransfer!.dropEffect = asyncValidation.isValid ? "move" : "none";
  isDragValid.value = asyncValidation.isValid;

  setDragState?.({
    dropTarget: props.folder,
    isValidDrop: asyncValidation.isValid,
  });
};

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault();
  if (isDragSource.value) return;
  isDragStart.value = true;
};

const handleDragLeave = (event: DragEvent) => {
  const relatedTarget = event.relatedTarget as HTMLElement;
  if (
    !relatedTarget ||
    !relatedTarget.closest(`[data-node-id="${props.folder.Id}"]`)
  ) {
    if (setDragState) {
      setDragState({
        ...dragState,
        dropTarget: null,
        isValidDrop: false,
      });
    }
  }

  const current = event.currentTarget as HTMLElement;
  const related = event.relatedTarget as HTMLElement | null;

  if (related && current.contains(related)) return;

  isDragStart.value = false;
};

const handleDrop = async (event: DragEvent) => {
  event.preventDefault();
  event.stopPropagation();

  const files = event.dataTransfer?.files;

  if((props.ruleAccess !== 2 || !props.folder.Editable))
  {
    isDragValid.value = false;
    isDragStart.value = false;
    return;
  }

  if (files && files.length > 0) {
    const isValidExternalTarget =
      props.folder.Id !== 0 &&
      !props.newDir &&
      props.folderType !== "systemProjectList" &&
      props.folder.Editable &&
      (containerProject?.value?.FileAccess === 2 || isAdmin);

    if (!isValidExternalTarget) {
      isDragStart.value = false;
      isDragValid.value = false;
      return;
    }

    const shouldOpenAfterDrop =
      props.folder.Id !== 0 &&
      props.folderType !== "systemFolder" &&
      isOpen.value == false;

    if (shouldOpenAfterDrop) {
      isOpen.value = true;

      if (emitToParent) {
        if (props.folderType == "projectFolder") {
          emitToParent({ type: "openProject", data: { dir: props.folder } });
        } else {
          emitToParent({
            type: "open",
            data: { dir: props.folder },
          });
        }
      }
    }

    isOpen.value = true;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const maxSize = 100 * 1024 * 1024;

      if (file.size > maxSize) {
        emitToParent?.({
          type: "error",
          data: {
            type: "error",
            errorType: "size",
          },
        });

        isDragStart.value = false;
        return;
      }

      if (file.type == "") {
        emitToParent?.({
          type: "error",
          data: {
            type: "error",
            errorType: "folder",
          },
        });

        isDragStart.value = false;
        return;
      }
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();

      isFileUploading.value = true;
      newFile.value.Name = file.name;
      formData.append("File", file);
      formData.append("FolderId", props.folder.Id.toString());
      formData.append("FileName", file.name);

      try {
        isDragStart.value = false;

        await emitToParent?.({
          type: "upload",
          data: {
            formdata: formData,
          },
        });
      } finally {
        isFileUploading.value = false;
        newFile.value.Name = file.name;
      }
    }

    isDragStart.value = false;
    isDragValid.value = true;

    return;
  }

  if (!dragState.isValidDrop) {
    isDragStart.value = false;
    return;
  }

  const shouldOpenAfterMove =
    props.folder.Id !== 0 &&
    props.folderType !== "systemFolder" &&
    isOpen.value === false;

  if (shouldOpenAfterMove && emitToParent) {
    if (props.folderType === "projectFolder") {
      emitToParent({ type: "openProject", data: { dir: props.folder } });
    } else {
      emitToParent({ type: "open", data: { dir: props.folder } });
    }
  }

  emitToParent?.({
    type: "move",
    data: {
      targetDir: props.folder,
      openAfterMove: shouldOpenAfterMove,
    },
  });

  isDragStart.value = false;
  isDragValid.value = true;

  setDragState?.({
    draggedNode: null,
    draggedElement: null,
    dropTarget: null,
    isValidDrop: false,
  });
};

const hasFolderNameConflict = (
  target: FolderOut,
  folderName: string,
): boolean => {
  return (target.Folders ?? []).some(
    (child) => child.Name.toLowerCase() === folderName.toLowerCase(),
  );
};

const hasFileNameConflict = (target: FolderOut, fileName: string): boolean => {
  return (target.Files ?? []).some(
    (child) => child.Name.toLowerCase() === fileName.toLowerCase(),
  );
};

const isDescendantFolder = (target: FolderOut, source: FolderOut): boolean => {
  const walk = (node: FolderOut): boolean => {
    if ((node.Folders ?? []).some((child) => child.Id === target.Id)) {
      return true;
    }

    return (node.Folders ?? []).some((child) => walk(child));
  };

  return walk(source);
};

const validateFolderDrop = (
  folder: FolderOut,
  target: FolderOut,
): DropValidationResult => {
  if (target.Id === 0) return { isValid: false };
  if (folder.Id === target.Id) return { isValid: false };
  if (target.isSelected === true) return { isValid: false };
  if (isDescendantFolder(target, folder)) return { isValid: false };
  if (hasFolderNameConflict(target, folder.Name)) return { isValid: false };

  return { isValid: true };
};

const validateFileDrop = (
  file: FileOut,
  parentFolder: FolderOut,
  target: FolderOut,
): DropValidationResult => {
  if (target.Id === 0) return { isValid: false };
  if (parentFolder.Id === target.Id) return { isValid: false };
  if (target.isSelected === true) return { isValid: false };
  if (hasFileNameConflict(target, file.Name)) return { isValid: false };

  return { isValid: true };
};

const validateSelectionDrop = (
  folders: FolderOut[],
  files: { file: FileOut; parentFolder: FolderOut }[],
  target: FolderOut,
): DropValidationResult => {
  if (target.Id === 0) return { isValid: false };
  if (target.isSelected === true) return { isValid: false };

  for (const folder of folders) {
    const result = validateFolderDrop(folder, target);
    if (!result.isValid) return result;
  }

  for (const item of files) {
    if (folders.some((folder) => folder.Id === item.parentFolder.Id)) {
      continue;
    }

    const result = validateFileDrop(item.file, item.parentFolder, target);
    if (!result.isValid) return result;
  }

  return { isValid: true };
};

const validateDrop = (
  draggedEntity: DragEntity,
  target: FolderOut,
): DropValidationResult => {
  if (target.Id === undefined) return { isValid: true };

  switch (draggedEntity.type) {
    case "folder":
      return validateFolderDrop(draggedEntity.folder, target);

    case "file":
      return validateFileDrop(
        draggedEntity.file,
        draggedEntity.parentFolder,
        target,
      );

    case "selection":
      return validateSelectionDrop(
        draggedEntity.folders,
        draggedEntity.files,
        target,
      );
  }
};


return (_ctx: any,_cache: any) => {
  const _component_file_folder = _resolveComponent("file-folder", true)!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", {
      ref_key: "folderRef",
      ref: folderRef,
      class: _normalizeClass(["folder", {
      folder_editable: _unref(isEditable),
      folder_searched: isSearched.value && _unref(searchMode),
      'folder--multiline': isMultiline.value,
      'folder-dragging-valid': _unref(isDragValid) && _unref(isDragStart),
      'folder-dragging-invalid': !_unref(isDragValid) && _unref(isDragStart),
    }]),
      style: _normalizeStyle(_unref(levelStyle)),
      onMouseenter: handleMouseEnter,
      onMouseleave: handleMouseLeave,
      draggable: 
      ((props.ruleAccess === 2 && _ctx.folder.Editable) || _unref(isAdmin)) &&
      _unref(advancedMode) &&
      _ctx.folderType === 'default' &&
      _unref(dragGeneralState).isValid &&
      _ctx.folder.isSelected === true
        ? true
        : false
    ,
      onDragstart: handleDragStart,
      onDragend: handleDragEnd,
      onDragover: handleDragOver,
      onDragenter: handleDragEnter,
      onDragleave: handleDragLeave,
      onDrop: handleDrop,
      onDrag: onDrag,
      id: _ctx.folder.Id,
      "data-folder-element": "true",
      onClick: handleOpenClick
    }, [
      (
        _unref(advancedMode) &&
        !_ctx.newDir &&
        _ctx.folderType !== 'systemFolder' &&
        _ctx.folderType !== 'systemProjectList'
      )
        ? (_openBlock(), _createBlock(AppCheckbox, {
            key: 0,
            class: "folder__checkbox",
            text: '',
            value: _ctx.folder.isSelected && !hasInaccessibleDescendants.value,
            onInput: selectFolder,
            disabled: (props.ruleAccess == 0 || !_ctx.folder.Editable) && !_unref(isAdmin),
            onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
          }, null, 8, ["value", "disabled"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_2, [
        (!_unref(isOpen))
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (
            _unref(isEditable) &&
            !_ctx.newDir &&
            ((props.ruleAccess == 2 && _ctx.folder.Editable) || _unref(isAdmin)) &&
            _ctx.folderType !== 'systemProjectList'
          )
                ? (_openBlock(), _createBlock(AppTooltip, {
                    key: 0,
                    text: _ctx.$t('file_create_folder'),
                    class: "folder__tooltip",
                    style: {"height":"18px"}
                  }, {
                    activator: _withCtx(() => [
                      _createVNode(AppIcon, {
                        class: "folder__icon",
                        name: "folder-closed-add",
                        width: "18px",
                        color: "#979797",
                        "hover-color": "#499259",
                        cursor: "pointer",
                        onClick: _withModifiers(createDir, ["stop"])
                      })
                    ]),
                    _: 1
                  }, 8, ["text"]))
                : (_openBlock(), _createBlock(AppIcon, {
                    key: 1,
                    class: "folder__icon",
                    name: "folder-closed",
                    width: "18px",
                    color: "#979797",
                    cursor: "pointer"
                  }))
            ], 64))
          : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
              (
            _unref(isEditable) &&
            ((props.ruleAccess == 2 && _ctx.folder.Editable) || _unref(isAdmin)) &&
            _ctx.folderType !== 'systemProjectList'
          )
                ? (_openBlock(), _createBlock(AppTooltip, {
                    key: 0,
                    text: _ctx.$t('file_create_folder'),
                    class: "folder__tooltip",
                    style: {"height":"18px"}
                  }, {
                    activator: _withCtx(() => [
                      _createVNode(AppIcon, {
                        class: "folder__icon",
                        name: "folder-open-add",
                        width: "18px",
                        color: "#979797",
                        "hover-color": "#499259",
                        cursor: "pointer",
                        onClick: _withModifiers(createDir, ["stop"])
                      })
                    ]),
                    _: 1
                  }, 8, ["text"]))
                : (_openBlock(), _createBlock(AppIcon, {
                    key: 1,
                    class: "folder__icon",
                    name: "folder-open",
                    width: "18px",
                    color: "#979797",
                    cursor: "pointer"
                  }))
            ], 64))
      ]),
      _createElementVNode("div", _hoisted_3, [
        ((props.ruleAccess == 2 && _ctx.folder.Editable))
          ? (_openBlock(), _createBlock(AppHashtag, {
              key: 0,
              ref: "hashtagRef",
              class: _normalizeClass(["folder__input-hashtag", {
          'folder__input-hashtag-no-empty': _ctx.folder.Tags?.length !== 0,
        }]),
              items: _ctx.folder.Tags,
              disabled: props.ruleAccess < 2 && !_unref(isAdmin),
              "tooltip-text": _ctx.$t('file_add_tag'),
              "selector-enabled": _ctx.folderType !== 'systemProjectList',
              "selector-value": selectedTags.value,
              "entity-type": _unref(TagEntityType).File,
              "tag-selector-mode": 'select',
              "entity-id": _ctx.folder.Id,
              "onUpdate:selectorValue": changeTags,
              onCreateTag: onFolderTagCreated,
              onRemove: removeTag
            }, null, 8, ["class", "items", "disabled", "tooltip-text", "selector-enabled", "selector-value", "entity-type", "entity-id"]))
          : (_openBlock(), _createBlock(AppHashtag, {
              key: 1,
              ref: "hashtagRef",
              class: _normalizeClass(["folder__input-hashtag", {
          'folder__input-hashtag-no-empty': _ctx.folder.Tags?.length !== 0,
        }]),
              items: _ctx.folder.Tags,
              "selector-enabled": _ctx.folderType !== 'systemProjectList',
              "selector-value": selectedTags.value,
              "entity-type": _unref(TagEntityType).File,
              "entity-id": _ctx.folder.Id,
              type: "text"
            }, null, 8, ["class", "items", "selector-enabled", "selector-value", "entity-type", "entity-id"])),
        _createElementVNode("span", _hoisted_4, [
          _withDirectives(_createElementVNode("span", {
            class: _normalizeClass({
            'folder__name-disable':
              (!_unref(isAdmin) && !_ctx.folder.Editable && _ctx.folderType == 'default'),
          }),
            innerHTML: highlightedName.value
          }, null, 10, _hoisted_5), [
            [_vShow, !_unref(isNameEditable) && _unref(searchMode)]
          ]),
          _withDirectives(_createVNode(FileInput, {
            "new-el": _ctx.newDir,
            ref_key: "inputRef",
            ref: inputRef,
            class: _normalizeClass(["folder__input", { 'folder__input--drag-mode': _unref(dragGeneralState).active }]),
            disabled: (props.ruleAccess == 0 || !_ctx.folder.Editable) && !_unref(isAdmin) && _ctx.folderType == 'default',
            "model-value": _ctx.folder.Name,
            "invalid-values": siblingFolderNames.value,
            "show-invalid-state": _ctx.folderType !== 'projectFolder',
            blocked: 
            !_unref(isAdmin) &&
            props.ruleAccess !== 2 &&
            !_ctx.folder.Editable &&
            _ctx.folderType !== 'systemProjectList'
          ,
            onEnter: renameFolder,
            onBlur: blurInput
          }, null, 8, ["new-el", "class", "disabled", "model-value", "invalid-values", "show-invalid-state", "blocked"]), [
            [_vShow, _unref(isNameEditable) || !_unref(searchMode)]
          ])
        ])
      ]),
      (
        !_ctx.newDir &&
        _ctx.folderType !== 'systemProjectList'
      )
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            ref_key: "menuAnchorRef",
            ref: menuAnchorRef,
            class: _normalizeClass(["folder__option-icons", {
        'folder__option-icons-invisible': !_unref(isEditable) || _unref(isNameEditable),
      }])
          }, [
            (
          _unref(isAdmin) ||
          (props.ruleAccess === 2 &&
            _ctx.folder.Editable)
        )
              ? (_openBlock(), _createBlock(AppTooltip, {
                  key: 0,
                  text: _ctx.$t('file_upload')
                }, {
                  activator: _withCtx(() => [
                    (
              _ctx.folderType == 'default' ||
              _ctx.folderType == 'projectFolder' ||
              _ctx.rootFolder
            )
                      ? (_openBlock(), _createBlock(AppIcon, {
                          key: 0,
                          class: "folder__option-icons-update",
                          name: "clip",
                          width: "16px",
                          color: "#979797",
                          cursor: "pointer",
                          "hover-color": "#499259",
                          onMousedown: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["prevent"])),
                          onClick: _cache[2] || (_cache[2] = _withModifiers(($event: any) => (chooseAction('upload')), ["stop"]))
                        }))
                      : _createCommentVNode("", true)
                  ]),
                  _: 1
                }, 8, ["text"]))
              : _createCommentVNode("", true),
            (_ctx.folder.Editable)
              ? (_openBlock(), _createBlock(FileDropdown, {
                  key: 1,
                  type: "menu",
                  class: "folder__option-icons-menu",
                  ref_key: "dropdownMenu",
                  ref: dropdownMenu,
                  onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"]))
                }, {
                  default: _withCtx(({ onSelect }) => [
                    (_ctx.folderType == 'systemFolder' || _ctx.folderType == 'projectFolder')
                      ? (_openBlock(), _createBlock(fileMenu, {
                          key: 0,
                          elements: menuElementsParent.value,
                          onSelect: 
              (option) => {
                setModalPositionNearDropdown();
                onSelect(option);
                chooseAction(option);
              }
            
                        }, null, 8, ["elements", "onSelect"]))
                      : (_openBlock(), _createBlock(fileMenu, {
                          key: 1,
                          elements: menuElementsInserted.value,
                          onSelect: 
              (option) => {
                setModalPositionNearDropdown();
                onSelect(option);
                chooseAction(option);
              }
            
                        }, null, 8, ["elements", "onSelect"]))
                  ]),
                  _: 1
                }, 512))
              : (_openBlock(), _createBlock(AppTooltip, {
                  key: 2,
                  text: _ctx.$t('file_view_information')
                }, {
                  activator: _withCtx(() => [
                    _createVNode(AppIcon, {
                      class: "folder__option-icons-update",
                      name: "info-circle-outline",
                      width: "16px",
                      color: "#979797",
                      cursor: "pointer",
                      "hover-color": "#499259",
                      onMousedown: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["prevent"])),
                      onClick: _cache[5] || (_cache[5] = _withModifiers(($event: any) => (chooseAction('info')), ["stop"]))
                    })
                  ]),
                  _: 1
                }, 8, ["text"]))
          ], 2))
        : _createCommentVNode("", true)
    ], 46, _hoisted_1),
    (
      _unref(isOpen) &&
      (_unref(isCreateDir) || _ctx.folder.Folders?.length || _ctx.folder.Files?.length)
    )
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          class: _normalizeClass(["folder__children", { 'folder__children--guided': _ctx.level > 0 }]),
          style: _normalizeStyle({ '--folder-guide-left': `${_ctx.level * 16 + 8}px` })
        }, [
          (_unref(isCreateDir))
            ? (_openBlock(), _createBlock(_component_file_folder, {
                "new-dir": true,
                key: 0,
                folder: _unref(newFolder),
                "sibling-folder-names": _ctx.folder.Folders.map((item) => item.Name),
                level: _ctx.level + 1,
                onCreate: createDirSuccess,
                "rule-access": props.ruleAccess
              }, null, 8, ["folder", "sibling-folder-names", "level", "rule-access"]))
            : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.folder.Folders, (item) => {
            return (_openBlock(), _createBlock(_component_file_folder, {
              "folder-type": 
        _ctx.folderType == 'systemFolder' && isSystemFolderName(item.Name, 'file_system_projects')
          ? 'systemProjectList'
          : _ctx.folderType == 'systemProjectList'
          ? 'projectFolder'
          : 'default'
      ,
              key: item.Id,
              folder: item,
              "parent-node": _ctx.folder,
              onOpen: ($event: any) => (_ctx.openFolder(item)),
              level: _ctx.level + 1,
              "rule-access": props.ruleAccess
            }, null, 8, ["folder-type", "folder", "parent-node", "onOpen", "level", "rule-access"]))
          }), 128)),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.folder.Files, (item) => {
            return (_openBlock(), _createBlock(FileString, {
              key: item.Id,
              file: item,
              level: _ctx.level + 1,
              "parent-node": _ctx.folder,
              onSelect: selectFile,
              onSelectFileAction: chooseFileAction,
              onRename: renameFile,
              onReplace: replaceFile,
              "rule-access": props.ruleAccess
            }, null, 8, ["file", "level", "parent-node", "rule-access"]))
          }), 128)),
          (isFileUploading.value)
            ? (_openBlock(), _createBlock(FileString, {
                key: 1,
                file: _unref(newFile),
                level: _ctx.level + 1,
                "parent-node": _ctx.folder,
                "is-uploading": true,
                onSelect: selectFile,
                onSelectFileAction: chooseFileAction,
                onRename: renameFile,
                onReplace: replaceFile,
                "rule-access": props.ruleAccess
              }, null, 8, ["file", "level", "parent-node", "rule-access"]))
            : _createCommentVNode("", true)
        ], 6))
      : _createCommentVNode("", true),
    _createElementVNode("input", {
      ref_key: "fileInputRef",
      ref: fileInputRef,
      type: "file",
      style: {"display":"none"},
      onChange: handleFileSelect
    }, null, 544)
  ], 64))
}
}

})